// FILE: src/pages/dealer/DealerLogin.jsx
import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDealerAuth } from "../../context/DealerAuthContext";
import "./DealerLogin.css";

const STEPS = {
  CHOOSE:      "choose",
  LOGIN_PHONE: "login_phone",
  LOGIN_OTP:   "login_otp",
  SIGNUP_FORM: "signup_form",
  SIGNUP_OTP:  "signup_otp",
};

// ─────────────────────────────────────────────────────────────────────────────
// OtpBoxes MUST live OUTSIDE DealerLogin so React never remounts it on render.
// Defining a component inside another function body causes React to treat it as
// a brand-new component type every render → unmount → focus lost.
// ─────────────────────────────────────────────────────────────────────────────
function OtpBoxes({ otp, otpRefs, onChange, onKeyDown, onPaste }) {
  return (
    <div className="dl-otp-row" onPaste={onPaste}>
      {otp.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { otpRefs.current[i] = el; }}
          className={`dl-otp-box${digit ? " dl-otp-box--filled" : ""}`}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          autoComplete="one-time-code"
          onChange={(e) => onChange(e, i)}
          onKeyDown={(e) => onKeyDown(e, i)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}

// PhoneInput also outside to keep it stable
function PhoneInput({ label = "Mobile Number", value, onChange, onEnter }) {
  return (
    <div className="dl-field">
      <label className="dl-label">{label}</label>
      <div className="dl-phone-wrap">
        <span className="dl-phone-prefix">+91</span>
        <input
          className="dl-input dl-input--phone"
          type="tel"
          maxLength={10}
          placeholder="10-digit number"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function DealerLogin() {
  const { loginWithPhone } = useDealerAuth();
  const navigate = useNavigate();

  const [step, setStep]           = useState(STEPS.CHOOSE);
  const [phone, setPhone]         = useState("");
  const [otp, setOtp]             = useState(["", "", "", "", "", ""]);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [form, setForm] = useState({
    name: "", address: "", shop_name: "",
    gst_number: "", aadhaar_image: null, aadhaar_preview: null,
  });

  const otpRefs = useRef([]);
  const fileRef = useRef();
  const API = process.env.REACT_APP_API_URL ? (process.env.REACT_APP_API_URL + '/api/v1') : '/api/v1';


  // ── OTP handlers ──────────────────────────────────────────────────────────
  const handleOtpChange = useCallback((e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
    if (val && idx < 5) {
      setTimeout(() => otpRefs.current[idx + 1]?.focus(), 0);
    }
  }, []);

  const handleOtpKeyDown = useCallback((e, idx) => {
    if (e.key === "Backspace") {
      if (otp[idx]) {
        setOtp((prev) => { const n = [...prev]; n[idx] = ""; return n; });
      } else if (idx > 0) {
        setOtp((prev) => { const n = [...prev]; n[idx - 1] = ""; return n; });
        setTimeout(() => otpRefs.current[idx - 1]?.focus(), 0);
      }
    } else if (e.key === "ArrowLeft"  && idx > 0) otpRefs.current[idx - 1]?.focus();
    else if   (e.key === "ArrowRight" && idx < 5) otpRefs.current[idx + 1]?.focus();
  }, [otp]);

  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!paste.length) return;
    const next = ["", "", "", "", "", ""];
    paste.split("").forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    setTimeout(() => otpRefs.current[Math.min(paste.length, 5)]?.focus(), 0);
  }, []);

  const resetOtp = () => setOtp(["", "", "", "", "", ""]);

  // ── Resend timer ──────────────────────────────────────────────────────────
  const startResendTimer = () => {
    setResendTimer(30);
    const iv = setInterval(() => {
      setResendTimer((t) => { if (t <= 1) { clearInterval(iv); return 0; } return t - 1; });
    }, 1000);
  };

  // ── API helpers ───────────────────────────────────────────────────────────
  const sendOtp = async (phoneNum) => {
    const res = await fetch(`${API}/dealer_phone_auth/send_otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone: phoneNum }),
    });
    return res.json();
  };

  // ── Step handlers ─────────────────────────────────────────────────────────
  const handleLoginSendOtp = async () => {
    if (!/^\d{10}$/.test(phone)) { setError("Please enter a valid 10-digit phone number"); return; }
    setError(""); setLoading(true);
    try {
      const data = await sendOtp(phone);
      if (data.error) throw new Error(data.error);
      setStep(STEPS.LOGIN_OTP);
      startResendTimer();
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (e) { setError(e.message || "Failed to send OTP"); }
    finally { setLoading(false); }
  };

  const handleLoginVerifyOtp = async () => {
    const otpStr = otp.join("");
    if (otpStr.length !== 6) { setError("Enter complete 6-digit OTP"); return; }
    setError(""); setLoading(true);
    try {
      await loginWithPhone(phone, otpStr);
      navigate("/dealer/dashboard");
    } catch (e) { setError(e.message || "Invalid OTP. Please try again."); }
    finally { setLoading(false); }
  };

  const handleSignupSendOtp = async () => {
    if (!form.name.trim())      { setError("Full name is required"); return; }
    if (!form.address.trim())   { setError("Address is required"); return; }
    if (!form.shop_name.trim()) { setError("Shop name is required"); return; }
    if (!form.aadhaar_image)    { setError("Aadhaar card photo is required"); return; }
    if (!/^\d{10}$/.test(phone)) { setError("Enter valid 10-digit phone number"); return; }
    if (form.gst_number && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(form.gst_number)) {
      setError("Invalid GST number format"); return;
    }
    setError(""); setLoading(true);
    try {
      const data = await sendOtp(phone);
      if (data.error) throw new Error(data.error);
      setStep(STEPS.SIGNUP_OTP);
      startResendTimer();
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (e) { setError(e.message || "Failed to send OTP"); }
    finally { setLoading(false); }
  };

  const handleSignupVerifyOtp = async () => {
    const otpStr = otp.join("");
    if (otpStr.length !== 6) { setError("Enter complete 6-digit OTP"); return; }
    setError(""); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("phone", phone);
      fd.append("otp", otpStr);
      fd.append("name", form.name);
      fd.append("address", form.address);
      fd.append("shop_name", form.shop_name);
      if (form.gst_number) fd.append("gst_number", form.gst_number);
      fd.append("aadhaar_image", form.aadhaar_image);

      const res  = await fetch(`${API}/dealer_phone_auth/verify_otp`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Registration failed");

      localStorage.setItem("dealerToken", data.token);
      localStorage.setItem("dealerUser",  JSON.stringify(data.user));
      localStorage.setItem("authToken",   data.token);
      navigate("/dealer/dashboard");
    } catch (e) { setError(e.message || "Invalid OTP. Please try again."); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await fetch(`${API}/dealer_phone_auth/resend_otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      resetOtp();
      startResendTimer();
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((f) => ({ ...f, aadhaar_image: file, aadhaar_preview: URL.createObjectURL(file) }));
  };

  // ── Shared OTP props object (avoids inline arrow fns causing child re-render) ──
  const otpProps = {
    otp,
    otpRefs,
    onChange:  handleOtpChange,
    onKeyDown: handleOtpKeyDown,
    onPaste:   handleOtpPaste,
  };

  return (
    <div className="dl-root">

      {/* ── LEFT PANEL ── */}
      <div className="dl-left">
        <div className="dl-left-inner">
          <div className="dl-brand">
            <span className="dl-brand-icon">🚗</span>
            <span className="dl-brand-name">CarYugo</span>
          </div>
          <h1 className="dl-tagline">
            The Dealer<br />
            <span className="dl-tagline-gold">Command Centre</span>
          </h1>
          <p className="dl-desc">
            Join India's fastest-growing dealer network. Access exclusive car listings,
            place live bids, and close deals — all from one powerful platform.
          </p>
          <div className="dl-features">
            {["Live Auction Listings", "Real-time Bidding", "Offer Tracking", "Deal Analytics"].map((f) => (
              <div className="dl-feature" key={f}>
                <span className="dl-feature-dot" />
                {f}
              </div>
            ))}
          </div>
          <div className="dl-deco-circle dl-deco-1" />
          <div className="dl-deco-circle dl-deco-2" />
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="dl-right">
        <div className={`dl-card ${step === STEPS.SIGNUP_FORM ? "dl-card--wide" : ""}`}>

          {/* CHOOSE */}
          {step === STEPS.CHOOSE && (
            <>
              <div className="dl-card-header">
                <div className="dl-avatar">🚗</div>
                <h2 className="dl-title">Dealer Portal</h2>
                <p className="dl-subtitle">Login or create your dealer account</p>
              </div>
              <div className="dl-choose-btns">
                <button className="dl-choose-btn dl-choose-btn--primary"
                  onClick={() => { setStep(STEPS.LOGIN_PHONE); setError(""); }}>
                  <span className="dl-choose-icon">📱</span>
                  <div><div className="dl-choose-label">Login</div><div className="dl-choose-sub">Sign in with OTP</div></div>
                  <span className="dl-choose-arrow">→</span>
                </button>
                <button className="dl-choose-btn dl-choose-btn--outline"
                  onClick={() => { setStep(STEPS.SIGNUP_FORM); setError(""); }}>
                  <span className="dl-choose-icon">✍️</span>
                  <div><div className="dl-choose-label">Sign Up</div><div className="dl-choose-sub">Create dealer account</div></div>
                  <span className="dl-choose-arrow">→</span>
                </button>
              </div>
              <p className="dl-note">New dealer? Fill in your details once and get verified by our team.</p>
            </>
          )}

          {/* LOGIN PHONE */}
          {step === STEPS.LOGIN_PHONE && (
            <>
              <div className="dl-card-header">
                <button className="dl-back" onClick={() => { setStep(STEPS.CHOOSE); setError(""); }}>← Back</button>
                <div className="dl-avatar">📱</div>
                <h2 className="dl-title">Dealer Login</h2>
                <p className="dl-subtitle">Enter your registered phone number</p>
              </div>
              <div className="dl-form">
                <PhoneInput value={phone} onChange={setPhone} onEnter={handleLoginSendOtp} />
                {error && <div className="dl-error">{error}</div>}
                <button className="dl-btn" onClick={handleLoginSendOtp} disabled={loading}>
                  {loading ? <span className="dl-spinner" /> : "Send OTP →"}
                </button>
                <p className="dl-switch-link">
                  Don't have an account?{" "}
                  <button className="dl-link" onClick={() => { setStep(STEPS.SIGNUP_FORM); setPhone(""); setError(""); }}>Sign up here</button>
                </p>
              </div>
            </>
          )}

          {/* LOGIN OTP */}
          {step === STEPS.LOGIN_OTP && (
            <>
              <div className="dl-card-header">
                <button className="dl-back" onClick={() => { setStep(STEPS.LOGIN_PHONE); resetOtp(); setError(""); }}>← Back</button>
                <div className="dl-avatar">🔐</div>
                <h2 className="dl-title">Verify OTP</h2>
                <p className="dl-subtitle">OTP sent to +91 {phone}</p>
              </div>
              <div className="dl-form">
                <OtpBoxes {...otpProps} />
                {error && <div className="dl-error">{error}</div>}
                <button className="dl-btn" onClick={handleLoginVerifyOtp} disabled={loading}>
                  {loading ? <span className="dl-spinner" /> : "Login to Dashboard →"}
                </button>
                <div className="dl-resend">
                  {resendTimer > 0
                    ? <span>Resend OTP in <strong>{resendTimer}s</strong></span>
                    : <button className="dl-link" onClick={handleResend}>Resend OTP</button>}
                </div>
              </div>
            </>
          )}

          {/* SIGNUP FORM */}
          {step === STEPS.SIGNUP_FORM && (
            <>
              <div className="dl-card-header">
                <button className="dl-back" onClick={() => { setStep(STEPS.CHOOSE); setError(""); }}>← Back</button>
                <div className="dl-avatar">✍️</div>
                <h2 className="dl-title">Dealer Sign Up</h2>
                <p className="dl-subtitle">Fill in your details to register</p>
              </div>
              <div className="dl-form dl-form--signup">
                <div className="dl-signup-grid">
                  <div className="dl-field">
                    <label className="dl-label">Full Name <span className="dl-req">*</span></label>
                    <input className="dl-input" placeholder="Your full name" value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="dl-field">
                    <label className="dl-label">Shop / Business Name <span className="dl-req">*</span></label>
                    <input className="dl-input" placeholder="e.g. Sharma Motors" value={form.shop_name}
                      onChange={(e) => setForm((f) => ({ ...f, shop_name: e.target.value }))} />
                  </div>
                  <div className="dl-field dl-field--full">
                    <label className="dl-label">Address <span className="dl-req">*</span></label>
                    <textarea className="dl-input dl-textarea" placeholder="Full business address" rows={2}
                      value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="dl-field dl-field--full">
                    <label className="dl-label">GST Number <span className="dl-optional-badge">Optional</span></label>
                    <input className="dl-input" placeholder="22AAAAA0000A1Z5" value={form.gst_number}
                      onChange={(e) => setForm((f) => ({ ...f, gst_number: e.target.value.toUpperCase() }))} />
                  </div>
                  <div className="dl-field dl-field--full">
                    <label className="dl-label">Aadhaar Card Photo <span className="dl-req">*</span></label>
                    <div className={`dl-upload-zone ${form.aadhaar_preview ? "dl-upload-zone--done" : ""}`}
                      onClick={() => fileRef.current?.click()}>
                      {form.aadhaar_preview ? (
                        <div className="dl-upload-preview">
                          <img src={form.aadhaar_preview} alt="Aadhaar" className="dl-aadhaar-img" />
                          <span className="dl-upload-change">Click to change</span>
                        </div>
                      ) : (
                        <div className="dl-upload-placeholder">
                          <span className="dl-upload-icon">📄</span>
                          <span className="dl-upload-text">Click to upload Aadhaar card</span>
                          <span className="dl-upload-hint">JPG, PNG or PDF · Max 5MB</span>
                        </div>
                      )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: "none" }} onChange={handleFileChange} />
                  </div>
                  <div className="dl-field dl-field--full">
                    <label className="dl-label">Mobile Number <span className="dl-req">*</span></label>
                    <div className="dl-phone-wrap">
                      <span className="dl-phone-prefix">+91</span>
                      <input className="dl-input dl-input--phone" type="tel" maxLength={10}
                        placeholder="10-digit number" value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} />
                    </div>
                    <p className="dl-field-hint">OTP will be sent to verify this number</p>
                  </div>
                </div>
                {error && <div className="dl-error">{error}</div>}
                <button className="dl-btn" onClick={handleSignupSendOtp} disabled={loading}>
                  {loading ? <span className="dl-spinner" /> : "Send OTP to Verify →"}
                </button>
                <p className="dl-switch-link">
                  Already registered?{" "}
                  <button className="dl-link" onClick={() => { setStep(STEPS.LOGIN_PHONE); setError(""); }}>Login here</button>
                </p>
              </div>
            </>
          )}

          {/* SIGNUP OTP */}
          {step === STEPS.SIGNUP_OTP && (
            <>
              <div className="dl-card-header">
                <button className="dl-back" onClick={() => { setStep(STEPS.SIGNUP_FORM); resetOtp(); setError(""); }}>← Back</button>
                <div className="dl-avatar">🔐</div>
                <h2 className="dl-title">Verify & Register</h2>
                <p className="dl-subtitle">OTP sent to +91 {phone}</p>
              </div>
              <div className="dl-form">
                <div className="dl-summary-card">
                  <div className="dl-summary-row"><span>Name</span><strong>{form.name}</strong></div>
                  <div className="dl-summary-row"><span>Shop</span><strong>{form.shop_name}</strong></div>
                  <div className="dl-summary-row"><span>Phone</span><strong>+91 {phone}</strong></div>
                  {form.gst_number && <div className="dl-summary-row"><span>GST</span><strong>{form.gst_number}</strong></div>}
                </div>
                <OtpBoxes {...otpProps} />
                {error && <div className="dl-error">{error}</div>}
                <button className="dl-btn" onClick={handleSignupVerifyOtp} disabled={loading}>
                  {loading ? <span className="dl-spinner" /> : "Complete Registration →"}
                </button>
                <div className="dl-resend">
                  {resendTimer > 0
                    ? <span>Resend OTP in <strong>{resendTimer}s</strong></span>
                    : <button className="dl-link" onClick={handleResend}>Resend OTP</button>}
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}